import Axios from 'axios';
import { ListResponse } from '../../types/list-response.model';
import { Listing } from '../../types/listing.model';
import moment from 'moment-timezone';

import { hopplerApi, HOPPLER_TIMEZONE } from '../../constants/api';
import { InquiryDocumentType } from '../../constants/inquiries';
import { formatWithTimezone } from '../core/date-formatter';
import { wait } from '../core/wait';

import { InquiryStage, InquiryListResponse, InquiryPipelineFiltersUiModel, InquiryPipelineParams, InquiryFTF, Inquiry, AddClientLead } from 'types/inquiries';
import { ViewingList, ViewingListPropertyGroup, ViewingListMetadata } from 'inquiries/viewing-list.model';
import { VlPropertyFilterParams } from 'inquiries/vl-property-filter-params.model';
import { VlPropertySearchResponse } from 'inquiries/vl-property-search-response.model';
import { InquiryDocument } from 'inquiries/inquiry-document.model';
import { StatusResponse } from '../../types/status-response.model';
import { DocumentShareResult } from 'inquiries/document-share-result.model';

class InquiryService {
  prepareParams(params: InquiryPipelineFiltersUiModel) {
    const filters = {
      ...params,
      locationId: params.location ? params.location.uId : undefined,
      location: undefined,
      locationSearchInput: undefined,
    } as InquiryPipelineParams;

    Object.keys(filters)
      .filter(key => key.startsWith('date'))
      .forEach(key => {
        if (filters[key]) {
          filters[key] = moment(filters[key]).tz(HOPPLER_TIMEZONE).format();
        }
      });

    return filters;
  }

  async getByStage(stage: InquiryStage, params?: InquiryPipelineFiltersUiModel) {
    params = params || {};

    const path = `${hopplerApi.host}/kumita/inquiry/pipeline/${stage}`;
    const filters = this.prepareParams(params);

    if (stage === 'for-collection') {
      filters.sort = 'collected';
      filters.sortBy = 'asc';
    }

    const response = await Axios.get<InquiryListResponse>(path, {
      withCredentials: true,
      params: filters || {}
    });
    return response.data;
  }

  async getInquiry(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}`;
    const response = await Axios.get<Inquiry>(path, { withCredentials: true });

    return response.data;
  }

  async getInquiryFtf(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/attachment/ftf`;

    const response = await Axios.get<InquiryFTF>(path, { withCredentials: true });
    return response.data;
  }

  async updateInquiryFtf(inquiryCode: string, ftf: InquiryFTF) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/attachment/ftf`;

    const response = await Axios.put<InquiryFTF>(path, ftf, { withCredentials: true });
    return response.data;
  }

  async getSelectedProperties(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/attachment/selected-listings`;

    const response = await Axios.get<ListResponse<Listing>>(path, { withCredentials: true });
    return response.data;
  }

  async search(search: string, stage: InquiryStage | 'all' = 'all', filters: InquiryPipelineFiltersUiModel = {}) {
    const path = `${hopplerApi.host}/kumita/inquiry/pipeline/${stage}`;
    const params = {
      search,
      ...this.prepareParams(filters)
    } as InquiryPipelineFiltersUiModel;

    const response = await Axios.get<InquiryListResponse>(path, {
      withCredentials: true,
      params
    });
    
    return response.data;
  }


  //////////////////////////////////////////////////////////////////////////////
  // Clients Control Parameters (CCP) functions
  // These are used to move inquiries through the stages.
  //////////////////////////////////////////////////////////////////////////////
  
  async setFirstCall(inquiryCode: string, payload: any) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/action/first-call-date`;
    const response = await Axios.post(path, payload, {
      withCredentials: true
    })
    return response.data;
  }

  async scheduleViewing(inquiryCode: string, date: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/action/viewing-scheduled`;
    const params = {
      date: formatWithTimezone(date)
    };
    const response = await Axios.post(path, params, {
      withCredentials: true
    })
    return response.data
  }


  //////////////////////////////////////////////////////////////////////////////
  // Viewing List
  // Endpoints documented in https://hoppler.atlassian.net/browse/IT-1588
  //////////////////////////////////////////////////////////////////////////////

  async getViewingLists(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/viewing-lists/list`;
    const response = await Axios.get<ListResponse<ViewingListMetadata>>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async getViewingList(inquiryCode: string, vlKey: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/viewing-lists/${vlKey}`;
    const response = await Axios.get<ViewingList>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async getViewingListTemplate(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/viewing-lists/create`;
    const response = await Axios.get<ViewingList>(path, { withCredentials: true });
    return response.data;
  }

   async getVLFinalList(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/viewing-list/final`;
    const response = await Axios.get<ViewingListPropertyGroup>(path, {
      withCredentials: true
    });

    return response.data;
   }

  async saveViewingList(inquiryCode: string, vl: ViewingList) {
    const metadata = vl.viewingList;
    let path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/viewing-lists`;

    if (metadata.vlKey) {
      path += `/${metadata.vlKey}`;
    }

    const response = await Axios.post<ViewingList>(path, vl, {
      withCredentials: true
    });

    return response.data;
  }

  async searchListingsForViewingList(inquiryCode: string, params: VlPropertyFilterParams) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/viewing-list/search`;
    const response = await Axios.get<VlPropertySearchResponse>(path, {
      withCredentials: true,
      params
    });

    return response.data;
  }

  //////////////////////////////////////////////////////////////////////////////
  // Offers
  // Endpoints documented in https://hoppler.atlassian.net/browse/IT-1590?focusedCommentId=10481
  //////////////////////////////////////////////////////////////////////////////
  async getOfferTemplateByListing(inquiryCode: string, propertyKey: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${propertyKey}`;
    const response = await Axios.get<InquiryDocument>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async getEmptyOfferTemplate(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer`;
    const response = await Axios.get<InquiryDocument>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async createOffer(inquiryCode: string, document: InquiryDocument) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer`;
    const response = await Axios.post<InquiryDocument>(path, document, {
      withCredentials: true
    });

    return response.data;
  }

  async updateOffer(inquiryCode: string, document: InquiryDocument) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${document.loiKey}`;
    const response = await Axios.put<InquiryDocument>(path, document, {
      withCredentials: true
    });

    return response.data;
  }


  async getOffer(inquiryCode: string, loiKey: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${loiKey}`;
    const response = await Axios.get<InquiryDocument>(path, {
      withCredentials: true
    });

    return response.data;
  }

  /**
   * Checks the document for an `loiKey`, and updates the offer if one is present
   * If the document has no `loiKey`, this creates a new offer. */
  saveOffer(inquiryCode: string, document: InquiryDocument) {
    if (document.loiKey) {
      return this.updateOffer(inquiryCode, document);
    } else {
      return this.createOffer(inquiryCode, document);
    }
  }

  async getOfferList(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/list`;
    const response = await Axios.get<ListResponse<InquiryDocument>>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async markOfferAsSent(inquiryCode: string, loiKey: string, msg: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${loiKey}/sent`;
    const payload = { msg };

    const response = await Axios.post<StatusResponse>(path, payload, {
      withCredentials: true
    });

    return response.data;
  }

  async updateSentOfferStatus(inquiryCode: string, loiKey: string, msg: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${loiKey}/sent`;
    const response = await Axios.post<StatusResponse>(path, { msg }, {
      withCredentials: true
    });

    return response.data;
  }

  /**
   * *Do this before uploading first image file.*
   * 
   * Marks the offer as having been accepted on the given date, or the current
   * date if a date is not specified.
   * 
   * @param inquiryCode 
   * @param offerCode 
   */
  async markOfferAsAccepted(inquiryCode: string, loiKey: string, dateAccepted: string) {
    const date = formatWithTimezone(dateAccepted);
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${loiKey}/accepted`;
    const response = await Axios.post<StatusResponse>(path, { date }, {
      withCredentials: true
    });

    return response.data;
  }

  async uploadOfferImageFiles(inquiryCode: string, loiKey: string, files: FileList) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/offer/${loiKey}/attachment`;
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      formData.append('file', file);
    }

    const response = await Axios.post<StatusResponse>(path, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true
    });

    return response.data;
  }

  //////////////////////////////////////////////////////////////////////////////
  // Contracts
  // Endpoints documented in https://hoppler.atlassian.net/browse/IT-1590?focusedCommentId=10482
  //////////////////////////////////////////////////////////////////////////////
  async getContractTemplates(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/templates`;
    const response = await Axios.get<ListResponse<InquiryDocument>>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async getEmptyContractTemplate(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract`;
    const response = await Axios.get<InquiryDocument>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async getContractTemplateByKey(inquiryCode: string, propOrLoiKey: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/${propOrLoiKey}`;
    const response = await Axios.get<InquiryDocument>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async getContracts(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/list`;
    const response = await Axios.get<ListResponse<InquiryDocument>>(path, {
      withCredentials: true
    });

    return response.data;
  }

  async updateContract(inquiryCode: string, document: InquiryDocument) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/${document.contractKey}`;
    const response = await Axios.put<InquiryDocument>(path, document, {
      withCredentials: true
    });

    return response.data;
  }

  async createContract(inquiryCode: string, document: InquiryDocument) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract`;
    const response = await Axios.post<InquiryDocument>(path, document, {
      withCredentials: true
    });

    return response.data;
  }

  async getContract(inquiryCode: string, contractKey: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/${contractKey}`;
    const response = await Axios.get<InquiryDocument>(path, {
      withCredentials: true
    });

    return response.data;
  }

  /**
   * Checks the document for an `contractKey`, and updates the offer if one is present
   * If the document has no `contractKey`, this creates a new offer. */
  saveContract(inquiryCode: string, document: InquiryDocument) {
    if (document.contractKey) {
      return this.updateContract(inquiryCode, document);
    } else {
      return this.createContract(inquiryCode, document);
    }
  }

  async uploadContractImageFiles(inquiryCode: string, contractKey: string, files: FileList) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/${contractKey}/attachment`;
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      formData.append('file', file);
    }

    const response = await Axios.post<StatusResponse>(path, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true
    });

    return response.data;
  }

  async markContractAsDealWon(inquiryCode: string, contractKey: string, date: string, amount: number) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/contract/${contractKey}/won`;
    const params = {
      date: formatWithTimezone(date),
      amount
    };

    const response = await Axios.post<InquiryDocument>(path, params, {
      withCredentials: true
    });

    return response.data;
  }

  //////////////////////////////////////////////////////////////////////////////
  // Document utilitiles
  //////////////////////////////////////////////////////////////////////////////

  getDocumentDownloadLink(inquiry: Inquiry, doc: InquiryDocument) {
    const documentType = !!doc.loiKey ? InquiryDocumentType.OFFER : InquiryDocumentType.CONTRACT;

    // Generated offer/contract documents are only supported for residential
    // inquiries.
    if (inquiry.division === 'Residential') {
      const inquiryCode = inquiry.inquiryCode;
      const docTypePath = documentType === InquiryDocumentType.OFFER ? 'offer' : 'contract';
      const key = documentType === InquiryDocumentType.OFFER ? doc.loiKey : doc.contractKey;

      return `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/${docTypePath}/${key}/download`;
    }
  }

  async downloadDocument(inquiry: Inquiry, doc: InquiryDocument) {
    const link = this.getDocumentDownloadLink(inquiry, doc);

    const response = await Axios.get<Blob>(link, {
      responseType: 'blob',
      withCredentials: true
    });

    return response.data;
  }

  async generateDocumentShareToken(inquiry: Inquiry, doc: InquiryDocument) {
    const inquiryCode = inquiry.inquiryCode;
    const documentType = doc.loiKey ? 'offer' : 'contract';
    const documentKey = doc.loiKey || doc.contractKey;
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/${documentType}/${documentKey}/token`;

    const response = await Axios.post<DocumentShareResult>(path, {}, {
      withCredentials: true
    });
    return response.data;
  }


  //////////////////////////////////////////////////////////////////////////////
  // Commissions
  //////////////////////////////////////////////////////////////////////////////

  async updateCommissionAmount(inquiryCode: string, hopplerAmount: number, coBrokerAmount: number) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/commission`;
    const payload = { commissionHoppler: hopplerAmount, commissionCoBroker: coBrokerAmount };

    const response = await Axios.post<StatusResponse>(path, payload, {
      withCredentials: true
    });

    return response.data;
  }

  //////////////////////////////////////////////////////////////////////////////
  // Collection
  //////////////////////////////////////////////////////////////////////////////

  async updateCollectionAmount(inquiryCode: string, amount: number) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/collection/amount`;
    const payload = { totalAmount: amount };

    const response = await Axios.put<StatusResponse>(path, payload, {
      withCredentials: true
    });
    
    // Weird quirk: if you POST to some APIs and immediately try to GET the
    // value you just sent, you'll get the old value.
    // For these cases, we add an additional 1-second wait.
    await wait(1000);

    return response.data;
  }

  async updateInvoiceNumber(inquiryCode: string, invoiceNumber: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/collection/invoice`;
    const payload = { invoiceNumber };

    const response = await Axios.put<StatusResponse>(path, payload, {
      withCredentials: true
    });
    await wait(1000);

    return response.data;
  }

  async toggleCollectionStatus(inquiryCode: string) {
    const path = `${hopplerApi.host}/kumita/inquiry/${inquiryCode}/collection/collected`;

    const response = await Axios.put<StatusResponse>(path, {}, {
      withCredentials: true
    });
    await wait(1000);

    return response.data;
  }


  //////////////////////////////////////////////////////////////////////////////
  // ADD CLIENT
  //////////////////////////////////////////////////////////////////////////////

  async addClient(lead: AddClientLead) {
    const path = `${hopplerApi.host}/kumita/inquiry/pipeline/add-client`;
    const response = await Axios.post<Inquiry>(path, lead, {
      withCredentials: true
    });

    return response.data;
  }
}

export const inquiryService = new InquiryService();
